/**
 * telegram-link service
 */

import {factories} from '@strapi/strapi';
import {KoniBotServiceType} from "../bot/koni-bot";
import {createHmac} from "node:crypto";

export interface TelegramUser {
  id: number
  first_name: string
  last_name?: string
  username?: string
  language_code?: string,
  allows_write_to_pm?: boolean
}

export interface ValidateInitDataResult {
  validData: boolean
  validTime: boolean
  params?: {
    query_id: string,
    user: TelegramUser | string,
    auth_date: number,
  }
}

export interface TelegramLinkData {
  id?: number,
  uid?: string,
  email?: string,
  address?: string,
  signature?: string,
  telegramId?: string,
  token?: string,
}

export interface TelegramLinkInfo extends TelegramLinkData {
  service: string,
  domain?: string,
}

export interface TelegramLinkParams<T> {
  initData: string
  bot: string,
  linkInfo?: T
}

export type QueryLinkParams = Pick<TelegramLinkData, 'id'|'uid'|'email'|'address'> & {
  service: string
  bot: string
  telegram_id?: number
}

export interface APIResult<T> {
  success: boolean
  error?: string
  data?: T
}

const TelegramLinkService = factories.createCoreService('plugin::telegram-bot.telegram-link', ({strapi}) => ({
  botService: strapi.plugin('telegram-bot').service<KoniBotServiceType>('koniBot'),
  telegramUserService: strapi.plugin('telegram-bot').service('telegram-user'),
  secretMap: {} as Record<string, Buffer>,
  getSecret(botName?: string) {
    const botNameKey = botName ?? '__default__';
    if (!this.secretMap[botNameKey]) {
      const token = this.botService.getBotHandler(botName).token;
      this.secretMap[botNameKey] = createHmac('sha256', 'WebAppData')
        .update(token)
        .digest();
    }

    return this.secretMap[botNameKey];
  },

  async link({initData, bot, linkInfo}: TelegramLinkParams<TelegramLinkInfo>, withSecretKey?: any) {
    const botHandler = this.botService.getBotHandler(bot);

    // Validate initData
    const rs = this.validateInitData(initData, bot);

    // @ts-ignore
    const telegramId = rs.params?.user?.id as number;

    // Find existed
    const telegramLinkDoc = strapi.db.query('plugin::telegram-bot.telegram-link');
    const existedLink = await telegramLinkDoc.findOne({
      where: {
        telegram_id: telegramId,
        telegram_bot_username: bot,
        service: linkInfo.service,
      }
    });

    // Validate change email
    const allowChangeEmail = strapi.plugin('telegram-bot').config<boolean>('user.allowChangeLinkingEmail');
    if (!allowChangeEmail && existedLink) {
      if (linkInfo.email != existedLink.link_email) {
        throw new Error("Cannot change email");
      }
    }

    // Todo: Issue-66 || Link to real Telegram User and Telegram Bot
    const linkData = {
      telegram_id: telegramId,
      telegram_bot_username: bot,
      service: linkInfo.service,
      link_domain: linkInfo.domain,
      link_id: linkInfo.id,
      link_uid: linkInfo.uid,
      link_email: linkInfo.email,
      link_token: linkInfo.token,
      link_address: linkInfo.address,
      link_signature: linkInfo.signature,
      use_secret: withSecretKey
    }

    let linkObject: any;
    // Upsert Linking
    let telegramUser = await this.telegramUserService.findUserByTelegramId(telegramId);
    const userInfo = rs.params?.user;

    if (!telegramUser) {
      telegramUser = await this.telegramUserService.upsertUser(userInfo, botHandler.botInfo);
    }

    linkData['telegram_user'] = telegramUser.id;
    linkData['telegram_bot'] = await botHandler.getBotModel().id;

    if (existedLink) {
      linkObject = await telegramLinkDoc.update({
        where: {
          id: existedLink.id,
        },
        data: linkData as any,
      });
    } else {
      // Issue-66 || Require secret to update link
      linkObject = await telegramLinkDoc.create({
        data: linkData,
      });
    }

    return {
      success: true,
      data: linkObject
    }
  },
  async getLink({service, bot, telegram_id, id, uid, email, address}: QueryLinkParams) {
    if (!telegram_id && !id && !uid && !email && !address) {
      return {
        success: false,
        error: 'Need at least one of telegram_id, id, uid, email, address'
      }
    }

    const telegramLinkDoc = strapi.db.query('plugin::telegram-bot.telegram-link');
    const conditions = {};
    if (telegram_id) {
      conditions['telegram_id'] = telegram_id;
    }
    if (id) {
      conditions['link_id'] = id;
    }
    if (uid) {
      conditions['link_uid'] = uid;
    }
    if (email) {
      conditions['link_email'] = email;
    }
    if (address) {
      conditions['link_address'] = address
    }
    const existedLink = await telegramLinkDoc.findOne({
      where: {
        telegram_bot_username: bot,
        service,
        ...conditions
      }
    });

    if (!existedLink) {
      return {
        success: false,
        error: 'Not found'
      }
    }

    return {
      success: true,
      data: existedLink
    }
  },

  validateInitData(initData: string, botName?: string): ValidateInitDataResult {
    const result = {
      validData: false,
      validTime: false
    } as ValidateInitDataResult;

    // This token re-create every time the pop-up is re-opened except reload the page => don't need to cache
    // Step 1: Parse the initData string
    const params = new URLSearchParams(initData);
    const receivedHash = params.get('hash');
    params.delete('hash');

    // Step 2: Create the data_check_string
    const dataCheckString = Array.from(params.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, value]) => `${key}=${value}`)
      .join('\n');

    // Step 3: Generate the HMAC-SHA-256 signature of the data_check_string
    const generatedHash = createHmac('sha256', this.getSecret(botName))
      .update(dataCheckString)
      .digest('hex');

    // Step 4: Compare the generated signature with the received hash
    result.validData = generatedHash === receivedHash;
    if (!result.validData) {
      return result
    }

    // Step 5: Extract the params data
    result.params = Object.fromEntries(params.entries()) as any;
    if (typeof result.params.user === 'string') {
      result.params.user = JSON.parse(result.params.user) as TelegramUser;
    }

    // Step 6: Optionally, check the auth_date to ensure the data is not outdated
    const authDate = parseInt(params.get('auth_date') || '0', 10);
    const currentTime = Math.floor(Date.now() / 1000);
    const maxAge = 86400; // 24 hours
    result.validTime = (currentTime - authDate) < maxAge;

    return result
  }
}));

export type TelegramLinkServiceType = ReturnType<typeof TelegramLinkService>;

export default TelegramLinkService;
